/** One work queue in the user's "my work" summary (server: AvaWorkQueue). */
export interface WorkQueue {
  key: string;
  title: string;
  screenId: string;
  count: number;
  unit: string;
  /** "chain" = only the tests the user works on (Analysis Department Chain); "lab" = whole lab. */
  scope: 'chain' | 'lab';
  action: string;
}

/** GET /api/v1/ava/my-work (and the "mywork" answer). */
export interface MyWork {
  queues: WorkQueue[];
  due: { overdue: number; dueToday: number; overdueJobs: number; dueTodayJobs: number } | null;
  hasChain: boolean;
  wholeLab: boolean;
  /** Set when the card is about one test / matrix / method ("Waiting for Alkalinity"). */
  subject?: string | null;
}

const plural = (unit: string, n: number) =>
  n === 1 ? unit : unit.endsWith('ch') || unit.endsWith('s') ? `${unit}es` : `${unit}s`;

/**
 * "My work" — one line per queue the user's role can open, with the count the
 * screen itself shows and an Open button. Counts come from the screens' own
 * readers on the server; this card only displays them.
 */
export function AvaMyWorkCard({ work, onOpen, onAsk }: {
  work: MyWork;
  onOpen: (screenId: string) => void;
  onAsk?: (question: string) => void;
}) {
  const due = work.due;
  return (
    <div className="ava-mywork">
      <div className="ava-mywork-hdr">
        <i className="ti ti-checklist" /> {work.subject ?? (work.wholeLab ? 'Waiting in the lab' : 'My work')}
      </div>
      {work.queues.length === 0 && (
        <div className="ava-mywork-empty">None of your screens has a work queue to count.</div>
      )}
      {work.queues.map((q) => (
        <div key={q.key} className={`ava-mywork-row${q.count === 0 ? ' zero' : ''}`}>
          <span className="ava-mywork-count">{q.count.toLocaleString()}</span>
          <span className="ava-mywork-text">
            <span className="ava-mywork-title">{q.title}</span>
            <span className="ava-mywork-sub">
              {plural(q.unit, q.count)} {q.action}{q.scope === 'lab' && !work.wholeLab ? ' · whole lab' : ''}
            </span>
          </span>
          <button type="button" className="ava-screen-open" onClick={() => onOpen(q.screenId)}>
            <i className="ti ti-external-link" /> Open
          </button>
        </div>
      ))}
      {due && (due.overdue > 0 || due.dueToday > 0) && (
        <div className="ava-mywork-due">
          {due.overdue > 0 && (
            <button type="button" className="ava-mywork-pill red" disabled={!!work.subject} onClick={() => onAsk?.('How many samples are overdue?')}>
              {due.overdue.toLocaleString()} overdue
            </button>
          )}
          {due.dueToday > 0 && (
            <button type="button" className="ava-mywork-pill amber" disabled={!!work.subject} onClick={() => onAsk?.('What is due today?')}>
              {due.dueToday.toLocaleString()} due today
            </button>
          )}
          <span className="ava-mywork-sub">{work.subject ? 'samples of this test' : work.wholeLab ? 'samples in the lab' : 'samples in your tests'}</span>
        </div>
      )}
      {!work.hasChain && !work.wholeLab && work.queues.some((q) => q.scope === 'chain') && (
        <div className="ava-mywork-note">
          No tests are assigned to you in the Analysis Department Chain, so the analysis queues are empty for you.
        </div>
      )}
    </div>
  );
}
